/**
 * The «Оновити» button of a page that is not finance.
 *
 * `refresh` starts a page-wide refresh (`lib/refresh.ts`): every mounted query
 * reads again, and those reads go past the server's cache to Todoist, Google,
 * Hevy, the weather and the rest — so a task added in the Todoist app shows up
 * on the press, not ten minutes later. `busy` runs from the press until the
 * page's own queries have landed, and a toast says how it went: the numbers on
 * screen may well not change, and a button whose only effect is a spinner
 * reads as one that did nothing.
 *
 * Finance tabs keep `useFinanceSync`: their «Оновити» is a Monobank sweep with
 * its own pacing, which this must never replace.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { requestRefresh } from "../lib/refresh";
import type { ApiState } from "./useApi";
import { useToast } from "./useToast";

/** Give up on the spinner (not the reads) after this long. */
const MAX_BUSY_MS = 20_000;
const TOAST_KEY = "page-refresh";

type Watched = Pick<ApiState<unknown>, "loading" | "refreshing" | "error">;

export interface PageRefresh {
  refresh: () => void;
  busy: boolean;
}

/**
 * @param states the page's own queries — what the spinner waits for
 * @param before page-specific extras to run first (e.g. emptying the day cache)
 */
export function usePageRefresh(states: readonly Watched[], before?: () => void): PageRefresh {
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const sawBusy = useRef(false);
  const beforeRef = useRef(before);
  beforeRef.current = before;

  // `loading` too: a query that never had data (it failed) re-reads as a load.
  const anyRefreshing = states.some((s) => s.refreshing || s.loading);
  const firstError = states.find((s) => s.error)?.error ?? null;

  const refresh = useCallback(() => {
    if (pending) return;
    sawBusy.current = false;
    setPending(true);
    beforeRef.current?.();
    requestRefresh();
  }, [pending]);

  // Done = the queries went busy after the press and have all come back.
  useEffect(() => {
    if (!pending) return;
    if (anyRefreshing) {
      sawBusy.current = true;
      return;
    }
    if (!sawBusy.current) return;
    setPending(false);
    if (firstError) {
      toast({ message: "Не вдалося оновити", detail: firstError, key: TOAST_KEY });
      return;
    }
    const at = new Date().toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" });
    toast({ message: "Оновлено", detail: `Свіжі дані на ${at}`, tone: "ok", key: TOAST_KEY });
  }, [pending, anyRefreshing, firstError, toast]);

  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setPending(false), MAX_BUSY_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);

  return { refresh, busy: pending || anyRefreshing };
}
